import React, { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

// Radix Popover+Calendar is unreliable on touch devices (taps not registered,
// overlay blocks the screen). On coarse-pointer devices we render a native
// <input type="date">, which opens the OS date picker and always works.
const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(pointer: coarse)').matches;

const toInputValue = (date) => (date ? format(date, 'yyyy-MM-dd') : '');

const fromInputValue = (v) => {
  if (!v) return null;
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const HybridDatePicker = ({
  value,
  onSelect,
  disabled, // solo aplica en desktop (Radix Calendar); en táctil se usa minDate via atributo min
  minDate,
  locale,
  placeholder = 'Seleccionar...',
  className,
  testId,
}) => {
  const [touch] = useState(isTouchDevice);
  const [open, setOpen] = useState(false);

  if (touch) {
    return (
      <div className="relative w-full">
        <CalendarIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <input
          type="date"
          value={toInputValue(value)}
          min={minDate ? toInputValue(minDate) : undefined}
          onChange={(e) => onSelect(fromInputValue(e.target.value))}
          data-testid={testId}
          style={{ colorScheme: 'dark' }}
          className={cn(
            'flex h-9 w-full min-w-0 appearance-none items-center rounded-md border border-input bg-transparent py-2 pl-9 pr-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-ring',
            !value && 'text-muted-foreground',
            className
          )}
        />
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('w-full justify-start text-left font-normal', !value && 'text-muted-foreground', className)}
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, 'PPP', { locale }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-card border-white/10" onInteractOutside={() => setOpen(false)}>
        <Calendar
          mode="single"
          selected={value}
          onSelect={(date) => {
            onSelect(date);
            setTimeout(() => setOpen(false), 50);
          }}
          disabled={disabled}
          initialFocus
          locale={locale}
        />
      </PopoverContent>
    </Popover>
  );
};
